import Adapter from '@ember-data/adapter';
import { once } from '@ember/runloop';
import { service as inject } from '@ember/service';
import { DEBUG } from '@glimmer/env';
import { decorateClass, isDecoratorArgs } from 'decorator-setup';

import { isPromise } from 'rise-ui/utils/promise.js';
import { method as callMethod } from 'rise-ui/utils/function.js';

/**
 * Class decorator to report on model network responses
 * @param {string} service Name of service to inject and report to
 * @param {string} method Name of method to call on service
 */
export default function modelReport(service, method = 'modelReport') {
  if (DEBUG && isDecoratorArgs(...arguments)) {
    throw new Error('@modelReport expects name of service to inject and report to');
  }
  return function(Target) {
    if (!Adapter.isPrototypeOf(Target)) {
      throw new Error(
        '@modelReport decorator should be applied to an Ember Adapter derived class',
      );
    }
    return decorateClass(class ModelReportDecoratedAdapter extends Target {
      @inject(service) _modelReportService;
      findAll(...args) {
        return this.#modelReportHook('findAll', ...args);
      }
      findMany(...args) {
        return this.#modelReportHook('findMany', ...args);
      }
      findRecord(...args) {
        return this.#modelReportHook('findRecord', ...args);
      }
      query(...args) {
        return this.#modelReportHook('query', ...args);
      }
      #modelReportHook(hook, ...args) {
        const result = super[hook](...args);
        if (isPromise(result)) {
          result.catch(() => {})
            .then(() => once(this, this.#modelReport));
        } else {
          once(this, this.#modelReport);
        }
        return result;
      }
      #modelReport() {
        callMethod(this._modelReportService, method);
      }
    });
  };
}
